import React, { useState, useEffect } from "react";
import SearchForm from "../../stories/Forms/SearchForm";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import {
  links,
  searchFormFields,
  clinicalPatientFields,
  getCrioLabel,
  vitalityClinicalFormFields,
  vitalitySearchFormFieldsNoDob,
} from "../../stories/Forms/fields";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import Table from "../../stories/Tables/Table";
import formatData, {
  formatReportedDate,
  clinicalstatusMapping,
  flattenObject,
  formatStudyName,
  formatClinicalDtAge,
  formatDtAge,
  STATUS_MAP,
  formatDateTime
} from "../../utils/utils";
import { Primary } from "../../stories/Buttons/Button.stories";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../../components/AuthContext/AuthContext";
import useApis from "../../services/useAPI";
import { checkClinicalPatientUrl } from "../../utils/constants";
import BackButton from "../../components/Shared/BackButton";
import ErrorToaster from "../../components/Shared/ErrorToaster";
import PageLayout from "../../components/Layout/PageLayout";
import VitalityDuplicatedTable from "../../stories/Tables/VitalityDuplicatedTable";
import DetailCard from "../../stories/Cards/DetailCard";
/**

 * ClinicalDetails component displays details of a patient record.
 * It includes a search form, a table for displaying modified bookings, and a back button.
 * @param {Object} props - Component props.
 * @param {Array} props.bookings - Modified bookings data.
 * @param {Object} props.user - User data.
 * @returns {JSX.Element|null} - Rendered component or null if user data is not available.
 */

function ClinicalDetails() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { callApi, resultPost, serverError, tokenError, toggleShowError } =
    useApis();
  let selectedService = localStorage.getItem("selectedService");
  const isVhLogin = auth?.featureFlag
  
  auth.setTitle(isVhLogin ? "PARTICIPANT RECORD" : "PARTICIPANT RECORD");
  
  const [fields, setFields] = useState(clinicalPatientFields);
  const stages = ["select-patient","patient-details","confirm-details","enter-barcode","appointment-confirmation"];
  
  useEffect(() => {
    if (!stages.includes(sessionStorage.getItem("allowedStage")) && auth?.featureFlag) {
      navigate("/patient-record");
    }
  },[sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    if (selectedService === "healthCare") {
      setFields(searchFormFields);
    } else {
      auth.setTitle("Clinical studies");
      setFields(
        isVhLogin
          ? vitalityClinicalFormFields
          : clinicalPatientFields.map((field) =>
              field.name === "crio_identifier"
                ? { ...field, label: getCrioLabel(isVhLogin) }
                : field,
            ),
      );
    }
  }, [selectedService, searchFormFields, clinicalPatientFields, vitalityClinicalFormFields, isVhLogin]);

  /**

   * Handles form submission for searching patient records.

   */
  const userData =
    resultPost?.user_info?.length > 0 ? resultPost.user_info[0] : null;

  const userInfo = userData
    ? {
        ...userData,
        date_of_birth: isVhLogin ? userData.date_of_birth : formatClinicalDtAge(userData.date_of_birth),
      }
    : null;  

  const handleSubmit = async (data) => {
    const formattedData = {
      ...formatData(data),
    };

    await callApi(formattedData, checkClinicalPatientUrl, "POST", {
      Authorization: `Bearer ${
        localStorage.getItem("access_token") || auth.accessToken
      }`,

      "Content-Type": "application/json",
    });
  };
 
  useEffect(() => {
    if (location.state?.data) {
      handleSubmit(
        location.state?.data
          ? location.state.data
          : location.state.resultPost.bookings[0],
      );
    }
  }, [location.state]);

  useEffect(() => {
    if (selectedService === "clinical") {
      localStorage.setItem("patientCard", "false");
    }
  }, [selectedService]);
  
    
   
  // Modify booking data for display
  const modifyBookings = (bookings) => {
    return bookings.map((booking, index) => {
      const bookingStatus =
        clinicalstatusMapping[booking.status?.replace(/-/g, "_")] || {};
      const isRegistered = booking.status === "registered";
      const isCreated = booking.status === "created";
      const isActivating =
        booking.status === "activating" &&
        booking.phlebotomist_booking_id !== "";

      /* Determine if hover should be disabled */
      const hover = isRegistered || isCreated || isActivating;

      return {
        id: index + 1,
        bookingId: booking.booking_id,
        testType: "Thrive Panel",
        sampleType: "Venous Blood",
        reportedDate:
          (booking.reported_date &&
            formatReportedDate(booking.reported_date)) ||
          "N/A",
        doctor: booking.reviewer_name ? booking.reviewer_name : " ",
        bookingStatus: bookingStatus,
        status: {
          component: (
            <Primary
              hover={hover}
              label={bookingStatus.label}
              onClick={(e) => {
                if (!isRegistered && !isCreated && !isActivating) {
                  e.preventDefault();
                } else {
                  auth?.allowStage("patient-details");
                  // Set patient card for clinical service, if needed
                  if (selectedService === "clinical") {
                    localStorage.setItem("patientCard", "true");
                  }

                  // Navigate based on booking status
                  if (booking.status === "registered") {
                    if(auth?.featureFlag){
                      navigate(`/enter-barcode/${booking.bid}`);
                    }
                    navigate(`/test-confirmation/${booking.bid}`);
                  } else {
                    // Both "created" and other statuses go to appointment confirmation
                    navigate(`/appointment-confirmation/${booking.bid}`, {
                      state: {
                        study:
                          resultPost?.study ||
                          formatStudyName("Thrive Registry"),
                      },
                    });
                  }
                }
              }}
              size="small"
              variant={bookingStatus.variant}
            />
          ),
        },
      };
    });
  };

  const getStatusDetails = (status, resultTs, isVhLogin = false) => {
    if (!status) return { className: "", label: "" };
    if (status === "rejected" && resultTs != null) {
      status = "error";
    }
    if (isVhLogin && status === "registered") {
      return { className: "", label: "Registered" };
    }
    const key = status.toLowerCase().replace(/\s+/g, "-");
    return STATUS_MAP[key] ?? { className: "", label: status };
  };
  

    const vhModifyBookings = (bookings, isVhLogin) =>
      bookings.map((booking, index) => {
        const { label } = getStatusDetails(
          booking?.status,
          booking?.result_ts,
          isVhLogin,
        );      
      const isRegistered = booking.status === "registered";
      const isCreated = booking.status === "created";
      const isActivating =
        booking.status === "activating" &&
        booking.phlebotomist_booking_id !== "";

      /* Determine if hover should be disabled */
      const hover = isRegistered || isCreated || isActivating;

  
        const canSelect = (
          ["registered", "quarantined", "activating","created"]
        ).includes(booking.status?.toLowerCase());
        return {
          id: index + 1,
          participantId: booking.patient_id,
          serviceName: booking.service_name,
          bookingId: booking.booking_id,
          testType: "Thrive Panel",
          sampleType: "-",
          reportedDate: booking.reported_date?.trim()
            ? formatDateTime(booking.reported_date)
            : null,
          statusDate: booking.status_ts ? formatDateTime(booking.status_ts) : null,
          doctor: booking.reviewer_name ?? " ",
          bookingStatusValue: booking.status,
          statusText: label,
          actionButton: canSelect ? (
            <button
              type="button"
              className="vh-table-action-btn"
             onClick={(e) => {
                if (!isRegistered && !isCreated && !isActivating) {
                  e.preventDefault();
                } else {
                  auth?.allowStage("patient-details");
                  // Set patient card for clinical service, if needed
                  if (selectedService === "clinical") {
                    localStorage.setItem("patientCard", "true");
                  }

                  // Navigate based on booking status
                  if (booking.status === "registered" && selectedService === "clinical") {
                    // VH clinical: details are already captured during staging, so skip test-confirmation
                    auth?.allowStage("enter-barcode");
                    navigate(`/enter-barcode/${booking.bid}`);
                  } else if (booking.status === "registered") {
                    navigate(`/test-confirmation/${booking.bid}`);
                  } else {
                    // Both "created" and other statuses go to appointment confirmation
                    navigate(`/appointment-confirmation/${booking.bid}`, {
                      state: {
                        study:
                          resultPost?.study ||
                          formatStudyName("Thrive Registry"),
                      },
                    });
                  }
                }
              }}>
              Select
            </button>
          ) : null,
        };
      });
  
  // Flatten and modify booking data
  const flattenedBookings = location.state?.resultPost?.bookings
    ? location.state.resultPost?.bookings.map((booking) =>
        flattenObject(booking),
      )
    : resultPost?.bookings?.map((booking) => flattenObject(booking)) || [];

  const modifiedBookings = flattenedBookings.length
    ? isVhLogin
      ? vhModifyBookings(flattenedBookings, isVhLogin)
      : modifyBookings(flattenedBookings)
    : [];

  const consultationId = flattenedBookings?.[0]?.bid ?? "";

  const displayUserInfo = {
    ...userInfo,
    booking_id: consultationId,
    ...(isVhLogin && userInfo?.first_name && userInfo?.last_name
      ? { full_name: `${userInfo.first_name} ${userInfo.last_name}` }
      : {}),
    ...(isVhLogin && userInfo?.date_of_birth
      ? { dob: formatDtAge(userInfo.date_of_birth, true) }
      : {}),
    ...(isVhLogin ? {
      sex: userInfo?.sex_at_birth
        ? userInfo.sex_at_birth.charAt(0).toUpperCase() + userInfo.sex_at_birth.slice(1)
        : null,
      phone_number_primary: userInfo?.phone_number_primary || userInfo?.mobile,
      email: userInfo?.email,
      ethnicity: userInfo?.ethnicity,
      crio_identifier: userInfo?.crio_identifier || "",
    } : {
      sex_at_birth: userInfo?.sex_at_birth
        ? userInfo.sex_at_birth.charAt(0).toUpperCase() + userInfo.sex_at_birth.slice(1)
        : null,
    }),
  };
  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <LoggedInLayout
      buttonLabel={"Logout"}
      img={img}
      isActivation={false}
      isVhlogin={isVhLogin}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      vitalityLayout={isVhLogin}
    >
      {isVhLogin ? (
        <PageLayout
          heading="Select a service"
          subHeading={
            <>
              To begin a registered service, click the{" "}
              <span className="boots-bold">"Select"</span> button.
            </>
          }
        >
          <div className="vh-patient-details-page clinical-service">
            <div className="container">
              {serverError && <span className="server-error">{serverError}</span>}
              <div className="row">
                <div className={`col-md-12 p-0`}>
                  <DetailCard
                    data={displayUserInfo}
                    fields={fields}
                  />
                  <div className="table-responsive">
                    <VitalityDuplicatedTable
                      data={modifiedBookings}
                      pagination={true}
                      sort={false}
                      columns={[
                        {
                          dataField: "participantId",
                          text: "Participant ID",
                        },
                        {
                          dataField: "testType",
                          text: "Study name",
                        },
                        {
                          dataField: "sampleType",
                          text: "Study type",
                        },
                        {
                          dataField: "statusText",
                          text: "Status",
                        },
                        {
                          dataField: "statusDate",
                          text: "Status date",
                        },
                        {
                          dataField: "actionButton",
                          text: "Action",
                          render: (value) => value,
                        },
                      ]}
                      statusLabel="Select"
                      pageSize={4}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageLayout>
      ) : (
        <div className="patient-details-page clinical-details-page">
          <div className="container">
            {serverError && <span className="server-error">{serverError}</span>}

            <div className="row">
              <div className="col-md-12">
                <SearchForm
                  colStructure={4}
                  data={userInfo}
                  fields={fields}
                  subtitle="Please select the test to continue"
                  child={
                    <div className="table-responsive">
                      <Table
                        data={modifiedBookings}
                        pagination={true}
                        search={true}
                        sort={false}
                      />
                    </div>
                  }
                />
                <BackButton onClick={handleBackClick} />
              </div>
            </div>
          </div>
        </div>
      )}
      <ErrorToaster error={tokenError} onClose={toggleShowError} />
    </LoggedInLayout>
  );
}

export default ClinicalDetails;
